import { ImageWithFallback } from "@/components/common/ImageWithFallback";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MoreHorizontal, Building2 } from "lucide-react";
import { useLingui } from "@lingui/react/macro";
import { format } from "date-fns";
import type { Company } from "@/lib/api/admin-api";

interface CompanyTileProps {
  company: Company;
  onViewDetails?: (company: Company) => void;
}

export function CompanyTile({ company, onViewDetails }: CompanyTileProps) {
  const { t } = useLingui();

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-(--color-border) bg-(--color-surface-muted) p-4 w-full">
      {/* Top row: logo + info + actions */}
      <div className="flex items-center justify-between gap-4">
        {/* Left: logo + text */}
        <div className="flex items-center gap-3.5 min-w-0">
          {/* Logo */}
          <div className="size-14 shrink-0 rounded-[10px] border border-(--color-border) bg-(--color-surface-content) overflow-hidden flex items-center justify-center">
            <ImageWithFallback
              src={company.logoUrl}
              alt={company.name}
              className="size-full object-cover"
              fallback={<Building2 className="size-6 text-(--color-text-secondary)/40" />}
            />
          </div>

          {/* Text block */}
          <div className="flex flex-col gap-1 min-w-0">
            {/* Name + status */}
            <div className="flex items-center gap-2">
              <span className="text-xl font-medium text-(--color-text-primary) leading-snug truncate">
                {company.name}
              </span>
              <Badge
                variant={company.isActive ? "success" : "secondary"}
                className={
                  company.isActive
                    ? "rounded-full border-transparent bg-(--color-status-active) text-(--brand-white) hover:bg-(--color-status-active)/90 shrink-0"
                    : "rounded-full shrink-0"
                }
              >
                {company.isActive ? t`Active` : t`Inactive`}
              </Badge>
            </div>

            {/* Sub-info */}
            <div className="flex items-center gap-2 text-sm whitespace-nowrap">
              {company.locations?.[0] && (
                <>
                  <span className="text-(--color-text-secondary)">{t`Address:`}</span>
                  <span className="text-(--color-text-primary)">
                    {[
                      company.locations[0].addressLine1,
                      company.locations[0].city,
                      company.locations[0].state,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </span>
                  <span className="text-(--gray-300)">•</span>
                </>
              )}
              <span className="text-(--color-text-secondary)">{t`Concept ID:`}</span>
              <span className="text-(--color-text-primary)">{company.slug}</span>
            </div>
          </div>
        </div>

        {/* Right: actions */}
        <div className="flex items-center gap-3 shrink-0">
          <button
            type="button"
            className="size-11 flex items-center justify-center rounded-[10px] border border-(--color-border) bg-(--color-surface-content) hover:bg-(--color-surface-accent) transition-colors"
          >
            <MoreHorizontal className="size-5 text-(--color-text-secondary)" />
          </button>
          <Button
            className="h-11 rounded-[10px] px-4 text-sm bg-(--color-action) hover:bg-(--color-action)/90 text-(--color-action-fg)"
            onClick={() => onViewDetails?.(company)}
          >
            {t`View Details`}
          </Button>
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-(--color-border)" />

      {/* Bottom row: stats + dates */}
      <div className="flex items-center justify-between text-sm">
        {/* Left: counts */}
        <div className="flex items-center gap-2 text-(--color-text-secondary)">
          <span>
            <span className="font-medium text-(--color-text-primary)">
              {company._count?.locations ?? 0}
            </span>{" "}
            {t`locations`}
          </span>
          <span className="text-(--gray-300)">•</span>
          <span>
            <span className="font-medium text-(--color-text-primary)">
              {company._count?.users ?? 0}
            </span>{" "}
            {t`users`}
          </span>
          <span className="text-(--gray-300)">•</span>
          <span>
            <span className="font-medium text-(--color-text-primary)">
              {company._count?.recipes ?? 0}
            </span>{" "}
            {t`recipes`}
          </span>
        </div>

        {/* Right: dates */}
        <div className="flex items-center gap-2 text-(--color-text-secondary) whitespace-nowrap">
          <span>{t`Created:`}</span>
          <span className="text-(--color-text-primary)">
            {format(new Date(company.createdAt), "dd-MM-yyyy")}
          </span>
          <span className="text-(--gray-300)">•</span>
          <span>{t`Updated:`}</span>
          <span className="text-(--color-text-primary)">
            {format(new Date(company.updatedAt), "dd-MM-yyyy")}
          </span>
        </div>
      </div>
    </div>
  );
}
